/**
 * A PESAGEM — o único momento em que a régua entra no app.
 *
 * Durante o ciclo inteiro o app estima: quantos peixes, quanto pesam, quanta
 * ração cabe. Nada disso é medido. A biometria é a exceção, e é ela que ancora
 * todo o resto — `/api/ronda` procura a última pesagem de qualquer fonte e é a
 * partir dela que a curva anda.
 *
 * ── É UMA OPERAÇÃO DE REPETIÇÃO ───────────────────────────────────────────
 *
 * O gestor descreveu assim: «conta, pesa, salva, + biometria, conta, pesa,
 * salva… encerra». Cinco ou seis repetições dão a média da operação.
 *
 * As repetições viram UMA linha no banco, com os totais somados — não cinco.
 * Cinco linhas no mesmo dia fariam a âncora do ciclo escolher uma delas por
 * acaso, e a escolhida seria pior que a soma das cinco. A média é ponderada
 * pelo mesmo motivo: um bloco de 30 peixes e outro de 5 não valem o mesmo.
 *
 * ── O RASCUNHO SOBREVIVE AO CELULAR ───────────────────────────────────────
 *
 * Os blocos em andamento ficam guardados no aparelho enquanto a operação não
 * encerra. Celular que trava, tela que bloqueia e navegador que recarrega
 * sozinho são rotina no campo, e perder cinco pesagens por causa disso faria
 * a próxima ser anotada no papel.
 *
 * ── E ELA GUARDA O PALPITE ANTES DA RESPOSTA ──────────────────────────────
 *
 * Junto do peso medido vai `peso_estimado_g`: o que o app dizia para aquele
 * peixe naquele dia. Esse número não entra em conta nenhuma. Existe porque um
 * estimador que nunca é confrontado com a balança não melhora, só acumula
 * confiança — e o confronto aparece na tela ANTES de gravar, para que quem
 * pesou possa dizer «essa amostra estava ruim» enquanto ainda está com o peixe
 * na mão.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  listarCiclos, parametrosDoCalculo, numeroDoParametro, registrarBiometria,
  type Ciclo, type MotivoDeBiometria, type ParametrosDoCalculo,
} from '../acesso/api'
import { pesoApos, type Curva } from '../dominio/crescimento'
import { g, inteiro, numero, numeroNaFaixa, soNumero } from '../dominio/unidades'
import { hojeISO, SeletorDeDia } from './SeletorDeDia'
import {
  apurar, blocoVazio, BlocosDePesagem, type Bloco,
} from './BlocosDePesagem'

const MOTIVOS: readonly { valor: MotivoDeBiometria; rotulo: string }[] = [
  { valor: 'rotina', rotulo: 'pesagem de rotina' },
  { valor: 'transferencia', rotulo: 'transferência' },
  { valor: 'despesca', rotulo: 'despesca' },
  { valor: 'conferencia', rotulo: 'conferência' },
]

const RASCUNHO = 'cunhatai.pesagem'

interface Rascunho {
  cicloId: string
  dia: string
  blocos: Bloco[]
}

function lerRascunho(): Rascunho | null {
  try {
    const bruto = localStorage.getItem(RASCUNHO)
    const r = bruto ? (JSON.parse(bruto) as Rascunho) : null
    return r && Array.isArray(r.blocos) && r.blocos.length > 0 ? r : null
  } catch {
    return null
  }
}

function guardarRascunho(r: Rascunho | null): void {
  try {
    if (r) localStorage.setItem(RASCUNHO, JSON.stringify(r))
    else localStorage.removeItem(RASCUNHO)
  } catch {
    /* armazenamento bloqueado: a operação vale só enquanto a tela estiver aberta */
  }
}

export function RegistrarBiometria({ aoGravar }: { aoGravar: () => void }) {
  const salvo = useMemo(lerRascunho, [])
  // Vira falso ao gravar: «retomando» depois de encerrar seria mentira.
  const [retomando, setRetomando] = useState(salvo !== null)

  const [ciclos, setCiclos] = useState<Ciclo[]>([])
  const [calculo, setCalculo] = useState<ParametrosDoCalculo | null>(null)
  const [cicloId, setCicloId] = useState(salvo?.cicloId ?? '')
  const [dia, setDia] = useState(salvo?.dia ?? hojeISO())
  const [porAmostra, setPorAmostra] = useState(true)
  const [blocos, setBlocos] = useState<Bloco[]>(salvo?.blocos ?? [blocoVazio()])
  const [medioDigitado, setMedioDigitado] = useState('')
  const [motivo, setMotivo] = useState<MotivoDeBiometria>('rotina')
  const [observacao, setObservacao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    Promise.all([listarCiclos(), parametrosDoCalculo()])
      .then(([c, p]) => {
        const abertos = c.filter((x) => !x.encerrado)
        setCiclos(abertos)
        // O ciclo guardado no rascunho pode ter sido encerrado desde ontem.
        // Se não casar com nenhum aberto, cai no primeiro — senão a tela fica
        // com um ciclo invisível e o botão de gravar não faz nada, calado.
        setCicloId((atual) =>
          abertos.some((x) => x.id === atual) ? atual : abertos[0]?.id ?? '',
        )
        setCalculo(p)
        setErro(null)
      })
      .catch((falha: unknown) => {
        setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
      })
      .finally(() => setCarregando(false))
  }, [])

  // Guarda o rascunho a cada mudança. Só quando há algo digitado: rascunho
  // vazio guardado faria a tela abrir «retomando» uma operação que não existe.
  useEffect(() => {
    const temAlgo = blocos.some((b) => b.quantidade !== '' || b.bruto !== '')
    guardarRascunho(temAlgo ? { cicloId, dia, blocos } : null)
  }, [cicloId, dia, blocos])

  const curva: Curva | null = useMemo(
    () =>
      calculo
        ? {
            a: numeroDoParametro(calculo.parametros, 'crescimento.curva.a', 0.135),
            b: numeroDoParametro(calculo.parametros, 'crescimento.curva.b', 0.00613),
            fator: numeroDoParametro(calculo.parametros, 'crescimento.fator_calibracao', 1),
          }
        : null,
    [calculo],
  )

  const ciclo = ciclos.find((c) => c.id === cicloId) ?? null
  const totalDosBlocos = apurar(blocos, true)
  const nMedio = numeroNaFaixa(medioDigitado, 1, 20000)
  const medido = porAmostra ? (totalDosBlocos?.mediaG ?? null) : nMedio

  /** O que a curva dizia para este peixe NESTE dia — o palpite a guardar. */
  const estimado = useMemo(() => {
    if (!ciclo || !curva) return null
    const ancora = ciclo.peso_medido_g ?? ciclo.peso_medio_g
    const daData = ciclo.data_medida ?? ciclo.data_povoamento
    if (!ancora || !daData) return null
    const dias = Math.max(0, Math.round((meiaNoite(dia) - meiaNoite(daData)) / 86_400_000))
    return pesoApos(g(ancora), dias, curva)
  }, [ciclo, curva, dia])

  if (carregando) return <p className="aviso">Carregando…</p>

  function gravar() {
    if (!ciclo || medido === null) return
    setErro(null)
    try {
      registrarBiometria(
        {
          ciclo_id: ciclo.id,
          data: dia,
          peso_medio_g: porAmostra ? null : medido,
          peixes_amostrados: porAmostra ? (totalDosBlocos?.peixes ?? null) : null,
          peso_amostra_kg: porAmostra ? (totalDosBlocos?.liquidoKg ?? null) : null,
          motivo,
          peso_estimado_g: estimado,
          observacao: observacao.trim() === '' ? null : observacao.trim(),
        },
        { rotulo: ciclo.tanque },
      )
      setBlocos([blocoVazio()])
      setMedioDigitado('')
      setObservacao('')
      guardarRascunho(null)
      setRetomando(false)
      aoGravar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gravar')
    }
  }

  if (ciclos.length === 0) {
    return <p className="aviso">Nenhum ciclo aberto. Não há peixe para pesar.</p>
  }

  return (
    <section className="bloco">
      <h2>Registrar pesagem</h2>

      {retomando && (
        <p className="secundario">
          Retomando uma pesagem que ficou aberta neste aparelho.
        </p>
      )}

      <label className="campo">
        <span className="campo-rotulo">Qual tanque?</span>
        <select value={cicloId} onChange={(e) => setCicloId(e.target.value)}>
          {ciclos.map((c) => (
            <option key={c.id} value={c.id}>
              {c.tanque} · {c.codigo}
            </option>
          ))}
        </select>
      </label>

      <SeletorDeDia dia={dia} aoMudar={setDia} />

      {/* DUAS FORMAS DE APURAR, as duas legítimas — regra 3. Contar e pesar é a
          operação de verdade, e por isso vem primeiro e marcada; o peso médio
          direto existe para quem recebeu o número do peixeiro por telefone. */}
      <div className="apuracao">
        <button
          type="button"
          className={porAmostra ? 'unidade-botao ativo' : 'unidade-botao'}
          onClick={() => setPorAmostra(true)}
        >
          contei e pesei
        </button>
        <button
          type="button"
          className={porAmostra ? 'unidade-botao' : 'unidade-botao ativo'}
          onClick={() => setPorAmostra(false)}
        >
          sei o peso médio
        </button>
      </div>

      {porAmostra ? (
        <BlocosDePesagem blocos={blocos} aoMudar={setBlocos} />
      ) : (
        <label className="campo">
          <span className="campo-rotulo">Peso médio (g)</span>
          <input
            value={medioDigitado}
            onChange={(e) => setMedioDigitado(soNumero(e.target.value))}
            inputMode="decimal"
            placeholder="640"
          />
        </label>
      )}

      <label className="campo">
        <span className="campo-rotulo">Por que pesou?</span>
        <select value={motivo} onChange={(e) => setMotivo(e.target.value as MotivoDeBiometria)}>
          {MOTIVOS.map((m) => (
            <option key={m.valor} value={m.valor}>{m.rotulo}</option>
          ))}
        </select>
      </label>

      <label className="campo">
        <span className="campo-rotulo">Observação (opcional)</span>
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="amostra pegou peixe graúdo da beira"
        />
      </label>

      <Confronto medido={medido} estimado={estimado} />

      {erro && <p className="aviso-erro">{erro}</p>}

      <button
        type="button"
        className="confirma"
        disabled={medido === null || ciclo === null}
        onClick={gravar}
      >
        ✓ registrar pesagem
      </button>
    </section>
  )
}

/**
 * A estimativa e a balança, lado a lado, antes de gravar.
 *
 * Duas linhas e nenhum adjetivo. A versão anterior anunciava «o peixe está 12%
 * ABAIXO do que o app supunha» em tarja avermelhada, e o gestor cortou o mesmo
 * texto na despesca pela mesma razão: o app não tem por que puxar orelha de
 * ninguém, e explicação demais só ocupa a tela de quem está em pé no sol.
 *
 * A diferença continua visível — são os dois números um do lado do outro, e
 * quem está com o peixe na mão sabe ler. O que sai é o veredito por escrito.
 *
 * O confronto continua servindo para o que foi feito: a pessoa vê o tamanho da
 * diferença enquanto ainda está com a amostra na mão e pode dizer «essa amostra
 * estava ruim» — informação que some se a conferência ficar para o escritório.
 */
function Confronto({
  medido, estimado,
}: {
  medido: number | null
  estimado: number | null
}) {
  if (medido === null) return null
  if (estimado === null) {
    return (
      <p className="secundario">
        Sem pesagem anterior neste ciclo, não há estimativa para comparar.
      </p>
    )
  }

  return (
    <p className="secundario">
      Estimativa do app {inteiro(g(estimado))} g · medido{' '}
      <strong>{inteiro(g(medido))} g</strong> ({numero(medido / 1000, 3)} kg por peixe).
    </p>
  )
}

function meiaNoite(iso: string): number {
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(a ?? 2026, (m ?? 1) - 1, d ?? 1).getTime()
}
